import { ShellBridge,BridgeError,isUiAction,type UiAction,type ServiceId,type ServiceContext } from '@wianco/portal-bridge';
import { config } from '@/config/env';
import { useAuthStore } from '@/features/auth/store';
import { filterAuthorizedServices } from '@/features/auth/utils/authorization';
import { registerShopUiFrame,setShopUiFrameActive,dispatchShopUiAction,resetShopUiSession,ensureShopCartContext,getShopCartContext } from './shopUiBridge';

const supported=['shop','academy','library','portfolio','support','community','booking','launchpad','marketplace'] as const;
export const isActionService=(value:string):value is typeof supported[number]=>supported.some(s=>s===value);
const frames=new Map<ServiceId,{bridge:ShellBridge;context:ServiceContext|null;active:boolean;registration:object}>();
const listeners=new Set<()=>void>();
const announce=()=>listeners.forEach(f=>f());
export const subscribeServiceActionContext=(listener:()=>void)=>{listeners.add(listener);return()=>{listeners.delete(listener);};};
export function getServiceActionContext(service:ServiceId):ServiceContext|null {
 if(service==='shop'){const cart=getShopCartContext();return cart?{ready:cart.ready}:null;}
 return frames.get(service)?.context??null;
}
function identity(service:ServiceId) {
 const state=useAuthStore.getState();
 if(!state.isAuthenticated||!state.user?.sub||!filterAuthorizedServices(config().EMBEDDED_SERVICES,state.user).some(s=>s.id===service))throw new BridgeError('SERVICE_NOT_ACTIVE');
 return JSON.stringify([state.user.sub,state.hubspotCustomerId,state.user.permissions,state.user.roles]);
}
export function registerPortalUiFrame(service:ServiceId,target:Window,origin:string,active:boolean) {
 if(service==='shop')return registerShopUiFrame(target,origin,active);
 frames.get(service)?.bridge.dispose();
 const registration={};
 const bridge=new ShellBridge(window,10000,undefined,service,context=>{
  const frame=frames.get(service);if(frame?.registration!==registration)return;
  if(JSON.stringify(context)!==JSON.stringify(frame.context)){frame.context=context;announce();}
 });
 frames.set(service,{bridge,context:null,active,registration});bridge.registerFrame(target,origin,active);announce();
 return()=>{if(frames.get(service)?.registration===registration){bridge.dispose();frames.delete(service);announce();}};
}
export function setPortalUiFrameActive(service:ServiceId,active:boolean) {
 if(service==='shop'){setShopUiFrameActive(active);return;}
 const frame=frames.get(service);if(frame){frame.active=active;frame.bridge.setActive(active);announce();}
}
export function resetPortalUiSession(){resetShopUiSession();for(const frame of frames.values())frame.bridge.reset();announce();}
async function waitForFrame(service:ServiceId,active=false) {
 if(frames.has(service)&&(!active||frames.get(service)?.active))return;
 await new Promise<void>((resolve,reject)=>{
  const ready=()=>{if(frames.has(service)&&(!active||frames.get(service)?.active)){clearTimeout(timer);listeners.delete(ready);resolve();}};
  const timer=setTimeout(()=>{listeners.delete(ready);reject(new BridgeError('NOT_READY'));},10000);
  listeners.add(ready);ready();
 });
}
async function waitForProtocol(bridge:ShellBridge) {
 const retry=window.setInterval(()=>bridge.handshake(),250);
 try { bridge.handshake(); await bridge.waitUntilReady(); }
 finally { window.clearInterval(retry); }
}
export async function ensureServiceActionContext(service:ServiceId) {
 if(service==='shop')return ensureShopCartContext();
 await waitForFrame(service);
 const frame=frames.get(service);if(!frame)return false;
 await waitForProtocol(frame.bridge);
 if(frame.context?.ready)return true;
 return new Promise<boolean>(resolve=>{
  const ready=()=>{if(frames.get(service)?.context?.ready){clearTimeout(timer);listeners.delete(ready);resolve(true);}};
  const timer=setTimeout(()=>{listeners.delete(ready);resolve(false);},10000);
  listeners.add(ready);ready();
 });
}
export async function dispatchPortalUiAction(action:UiAction,navigate:(path:string)=>void) {
 if(!isUiAction(action))throw new BridgeError('INVALID_PAYLOAD');
 if(action.service==='shop')return dispatchShopUiAction(action,navigate);
 const current=identity(action.service);
 if(!frames.get(action.service)?.active){navigate('/'+action.service);await waitForFrame(action.service,true);}
 if(identity(action.service)!==current)throw new BridgeError('STALE_FRAME');
 const frame=frames.get(action.service);if(!frame)throw new BridgeError('NOT_READY');
 await waitForProtocol(frame.bridge);
 if(identity(action.service)!==current)throw new BridgeError('STALE_FRAME');
 await frame.bridge.dispatch(action);
 if(identity(action.service)!==current)throw new BridgeError('STALE_FRAME');
}
